import { QueryClient } from "@tanstack/react-query";

/**
 * The shared TanStack Query client for the app.
 *
 * Owns server state (data fetched from the MyYoast API via the Orval-generated hooks).
 * Redux remains responsible for client/UI state. A single instance is created here and
 * provided at the app root via `QueryClientProvider`.
 *
 * **Data is always revalidated on mount, focus and reconnect.** The five-minute `staleTime` and
 * disabled focus refetching these replace came from the first migrated route, a profile form where
 * nothing else could change the data. That does not hold generally: most of what this app shows can be
 * changed from another page, another tab, or the shop — disconnect a subscription on its detail page and
 * navigate back, and a cached site page would still offer to activate it, then fail with a 403 from the
 * server that knows better.
 *
 * Cross-page invalidation would be the precise fix, but it only works between queries and mutations that
 * both run through TanStack, and most of this app is still Redux-backed. Until that changes, revalidating
 * is the only thing that can catch a change made outside the cache's knowledge.
 *
 * It is affordable because these read models are small by construction — the endpoints migrated so far
 * answer in tens of milliseconds with kilobytes, not the megabyte collections they replaced. `gcTime` is
 * kept, so a revalidating query still renders its cached data instead of flashing a loader.
 */
export const queryClient = new QueryClient( {
	defaultOptions: {
		queries: {
			staleTime: 0,
			gcTime: 1000 * 60 * 10,
			retry: 1,
			refetchOnWindowFocus: true,
			refetchOnReconnect: true,
		},
	},
} );

export default queryClient;
